import { SuggestItems } from '@doda/common/src/interfaces/suggestDispatcher';
import { MouseEvent, useState, useCallback } from 'react';
import { KEYWORD_OPTIONS } from 'components/KeywordSuggest/constants';
import { getSuggestDispatcher } from '@doda/common/src/services/suggestDispatcherAPI';
import { IncludedKeywordCategoryRequestCode } from '@doda/api-client/aspida/@types';
import { useRouter } from 'next/router';
import { DODA_SITE_URL } from '@doda/common/src/constants/urls';
import { useAppDispatch } from '@doda/common/src/redux/hooks';
import { measureKeywordSearch } from '../../logics/analytics';

const MIN_INPUT_LENGTH = 3;

interface ArgumentType {
  defaultValue: string;
  defaultCategory: IncludedKeywordCategoryRequestCode;
}

/**
 * キーワードに関するhooks
 * @param param.defaultValue
 * @param param.defaultCategory
 * @returns 入力状態と更新処理
 */
export const useSearchKeyword = ({
  defaultValue,
  defaultCategory,
}: ArgumentType) => {
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState(defaultValue.replace(/,/g, ' '));
  const [suggests, setSuggests] = useState<SuggestItems['items']>([]);
  const [searchCategory, setSearchCategory] =
    useState<IncludedKeywordCategoryRequestCode>(defaultCategory);
  const { pathname } = useRouter();
  const isJobSearchList =
    pathname === DODA_SITE_URL.jobSearchList.replace('.action', '');

  const onChangeText = useCallback(
    (event: string) => {
      const inputText = event;
      setSearchText(inputText);

      if (inputText.length < MIN_INPUT_LENGTH) {
        setSuggests([]);
        return;
      }

      if (inputText.length >= MIN_INPUT_LENGTH) {
        // 検索結果一覧の場合のみ計測を行う
        if (isJobSearchList) dispatch(measureKeywordSearch());
        // 大量のアクションが発行されそうなので、直接レスポンスを使う
        getSuggestDispatcher(
          KEYWORD_OPTIONS.find((item) => item.value === searchCategory)?.type ||
            KEYWORD_OPTIONS[0].type,
          inputText
        )
          .then((res) => {
            setSuggests(res);
          })
          .catch();
      }
    },
    [dispatch, isJobSearchList, searchCategory]
  );

  const onClickSuggest = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setSearchText(event.currentTarget.value);
    setSuggests([]);
  }, []);

  const onChangeUpdateKeyword = useCallback(
    (value: IncludedKeywordCategoryRequestCode) => {
      setSearchCategory(value);
    },
    []
  );

  const onFocusInput = useCallback(() => {
    if (searchText.length >= MIN_INPUT_LENGTH) {
      getSuggestDispatcher(
        KEYWORD_OPTIONS.find((item) => item.value === searchCategory)?.type ||
          KEYWORD_OPTIONS[0].type,
        searchText
      )
        .then((res) => {
          setSuggests(res);
        })
        .catch();
    }
  }, [searchText, searchCategory]);

  const resetSuggests = useCallback(() => {
    setSuggests([]);
  }, []);

  return {
    state: {
      searchText,
      suggests,
      searchCategory,
    },
    updateFunctions: {
      onChangeText,
      onClickSuggest,
      onChangeUpdateKeyword,
      onFocusInput,
      resetSuggests,
    },
  };
};
